import { type FieldValues } from 'react-hook-form';

import {
  type IdValidationConfigPreset,
  type OrganizationDTO,
  type OrganizationUpdateRequestDTO,
  OrganizationUpdateRequestDTOTalentUniqueFieldsItem,
  type PlatformLocale,
  type UserIdentifier
} from '@/api/generated';
import { PROCESS_SETTINGS_STEP_KEYS } from '@/root/linker';
import {
  type AccessInvitationFormValues,
  type TalentRegistrationFormValues,
  type TestPropertiesFormValues
} from '@/views/ProcessSettingsForm/defaults';

export interface OrganizationSettingsFieldValues
  extends Omit<OrganizationUpdateRequestDTO, 'talentUniqueFields' | 'admins'>,
    FieldValues {
  /** Admins as UserIdentifier: { id } for existing users, { email, phone } for new invites */
  admins?: UserIdentifier[];
  authConfig?: OrganizationSettingsFieldValuesAuthConfig;
  [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]?: AccessInvitationFormValues;
  [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION]?: TalentRegistrationFormValues;
  [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES]?: TestPropertiesFormValues;
  userInviteEmailLanguage?: PlatformLocale;
  isEnterprise?: boolean;
  idValidation?: OrganizationSettingsFieldValuesIdValidationConfig;
  talentUniqueFields?: OrganizationUpdateRequestDTOTalentUniqueFieldsItem;
  norms?: OrganizationSettingsFieldValuesNorms;
}

export enum ID_STRUCTURE_OPTIONS {
  LETTERS = 'letters',
  NUMBERS = 'numbers',
  BOTH = 'both'
}

export interface OrganizationSettingsFieldValuesAuthConfig {
  invalidateAfterDays?: number;
  methods?: string[];
}

export interface OrganizationSettingsFieldValuesIdValidationConfig {
  preset?: IdValidationConfigPreset;
  maxLength?: number;
  structure?: ID_STRUCTURE_OPTIONS;
}

export interface OrganizationSettingsFieldValuesNormItem {
  defaultNormId: string;
  testTypeId: string;
  testName?: string;
}

export interface OrganizationSettingsFieldValuesNorms {
  items: OrganizationSettingsFieldValuesNormItem[];
  defaultNormId?: string;
}

export type OrganizationData = Omit<OrganizationUpdateRequestDTO, 'admins' | 'keyAccountManagers' | 'offices'> &
  OrganizationDTO;
